/**
 * AutoWahControls Component
 * 
 * Specialized control panel for the Auto-Wah effect.
 * Displays 7 rotary knobs with frequency indicator and filter mode selector.
 */

import { useMemo } from 'react';
import { RotaryKnob } from '../../ui/RotaryKnob';
import { FrequencyIndicator } from './FrequencyIndicator';
import { PresetSelector } from './PresetSelector';
import type { EffectInstance, EffectDefinition } from '../../../services/effects/EffectsManager';
import { getParameterDescription } from '../../../constants/effectDescriptions';
import './AutoWahControls.css';

/**
 * Props for AutoWahControls component
 */
export interface AutoWahControlsProps {
  /** Effect instance */
  effect: EffectInstance;
  /** Effect definition */
  effectDefinition: EffectDefinition;
  /** Callback when parameter changes */
  onParameterChange: (parameterId: string, value: number) => void;
  /** Callback when multiple parameters change at once (for presets) */
  onParametersChange?: ((parameters: Record<string, number>) => void) | undefined;
  /** Whether controls are disabled */
  disabled?: boolean;
}

/**
 * AutoWahControls component
 * 
 * Layout:
 * - Top row: 4 knobs (minFreq, maxFreq, sensitivity, resonance)
 * - Bottom row: 3 knobs (attack, release, mix)
 * - Frequency indicator showing current filter frequency
 * 
 * @param props - Component props
 * @returns AutoWahControls component
 */
export function AutoWahControls({
  effect,
  effectDefinition,
  onParameterChange,
  onParametersChange,
  disabled = false,
}: AutoWahControlsProps) {
  /**
   * Get parameter definition by ID
   */
  const getParamDef = (id: string) => {
    return effectDefinition.parameters.find(p => p.id === id);
  };

  /**
   * Get parameter value
   */
  const getParamValue = (id: string) => {
    const paramDef = getParamDef(id);
    return effect.parameters[id] ?? paramDef?.default ?? 0;
  };

  /**
   * Calculate current filter frequency (simulated for display)
   * In a real implementation, this would come from the audio processor
   */
  const currentFrequency = useMemo(() => {
    const minFreq = getParamValue('minFreq');
    const maxFreq = getParamValue('maxFreq');
    const sensitivity = getParamValue('sensitivity');
    
    // Simulate envelope-controlled frequency
    // This is a simplified calculation for visualization
    const envelopeLevel = 0.5; // Assume mid-level envelope
    
    return minFreq + (maxFreq - minFreq) * envelopeLevel * (sensitivity / 100);
  }, [effect.parameters]);

  /**
   * Handle preset selection
   */
  const handlePresetSelect = (presetParameters: Record<string, number>) => {
    // Use batch update if available, otherwise fall back to individual updates
    if (onParametersChange) {
      onParametersChange(presetParameters);
    } else {
      // Fallback: Apply parameters individually with delays
      Object.entries(presetParameters).forEach(([paramId, value], index) => {
        setTimeout(() => {
          onParameterChange(paramId, value);
        }, index * 10);
      });
    }
  };

  return (
    <div className="autowah-controls">
      {/* Preset Selector */}
      <PresetSelector
        effectType={effect.type}
        onPresetSelect={handlePresetSelect}
        disabled={disabled}
      />

      <div className="autowah-controls-main">
        {/* Top row: Min Freq, Max Freq, Sensitivity, Resonance */}
        <div className="autowah-controls-row">
          {['minFreq', 'maxFreq', 'sensitivity', 'resonance'].map((paramId) => {
            const paramDef = getParamDef(paramId);
            if (!paramDef) return null;
            
            const value = getParamValue(paramId);
            const tooltip = getParameterDescription(effect.type, paramId);
            
            return (
              <RotaryKnob
                key={paramId}
                value={value}
                min={paramDef.min}
                max={paramDef.max}
                default={paramDef.default}
                step={paramDef.step}
                label={paramDef.name}
                {...(paramDef.unit && { unit: paramDef.unit })}
                onChange={(val) => onParameterChange(paramId, val)}
                disabled={disabled}
                size="medium"
                {...(tooltip && { tooltip })}
              />
            );
          })}
        </div>

        {/* Bottom row: Attack, Release, Mix */}
        <div className="autowah-controls-row">
          {['attack', 'release', 'mix'].map((paramId) => {
            const paramDef = getParamDef(paramId);
            if (!paramDef) return null;
            
            const value = getParamValue(paramId);
            const tooltip = getParameterDescription(effect.type, paramId);
            
            return (
              <RotaryKnob
                key={paramId}
                value={value}
                min={paramDef.min}
                max={paramDef.max}
                default={paramDef.default}
                step={paramDef.step}
                label={paramDef.name}
                {...(paramDef.unit && { unit: paramDef.unit })}
                onChange={(val) => onParameterChange(paramId, val)}
                disabled={disabled}
                size="medium"
                {...(tooltip && { tooltip })}
              />
            );
          })}
        </div>
      </div>

      {/* Frequency Indicator */}
      <div className="autowah-controls-indicator">
        <FrequencyIndicator
          frequency={currentFrequency}
          minFreq={getParamValue('minFreq')}
          maxFreq={getParamValue('maxFreq')}
          width={280}
          height={50}
        />
      </div>
    </div>
  );
}
